"use client";

import styles from "@/styles/category/index.module.scss";
import Image from "next/image";
import { useState, useEffect } from "react";
import categoryApi from "@/service/modules/categoryApi";
import { BreadCrumbItem, ProductType } from "@/types/product";
import dayjs from "dayjs";
import ProductItem from "@/components/global/ProductItem";
import ProductCardFullWidth from "@/components/global/ProductCardFullWidth";
import FilterBox from "@/components/category/FilterBox";
import { Loading } from 'react-vant';
import { setGtmDataLayer } from "@/utils/gtmDataLayer";
import { matomoSetCustomDimension } from "@/utils/matomoEvent"
import { getCookie } from "@/utils/cookie";

// 获取分类列表数据 - 使用React cache避免重复请求
const getCategoryList = async (params: any) => {
  try {
    // 清空params中的空值键值对
    const dataObj = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== null && value !== ""
      )
    );

    const productData = await categoryApi.getCategoryProduct(dataObj);

    if (
      productData.data &&
      productData.data.pageList &&
      productData.data.pageList.items &&
      productData.data.pageList.items.length
    ) {
      productData.data.pageList.items.forEach((item: any) => {
        item.imgUrl = item.filename || item.newThumbnail;
      });
    }

    return {
      ...productData.data,
    };
  } catch (error) {
    console.log(
      "Next.js Error :",
      dayjs().format("YY-MM-DD HH:mm:ss"),
      `  Page Category: ${params.categoryPath} Data acquisition failed:`,
      JSON.stringify(error)
    );
  }
};

interface PageListType {
  items: ProductType[];
  total: number;
  page: number;
  perPage: number;
}

interface CategoryContentProps {
  breadCrumb: BreadCrumbItem[];
  pageList: PageListType;
  categoryPath: string;
  otherParam: string;
  sidebarFilter: any[];
}

export default function CategoryContent({
  breadCrumb,
  pageList,
  categoryPath,
  otherParam,
  sidebarFilter,
}: CategoryContentProps) {
  // 将传入的 sidebarFilter 存储到本地状态，便于页面重新请求时保持和重新赋值
  const [localSidebarFilter, setLocalSidebarFilter] = useState<any[]>(
    sidebarFilter ? sidebarFilter : []
  );
  // 维护请求参数对象（用于 getCategoryList）
  const [queryParams, setQueryParams] = useState<any>({
    categoryPath,
    otherParam,
    page: pageList?.page ? pageList.page : 1,
    perPage: 16,
  });
  // 维护本地的产品列表数据（由接口请求后更新）
  const [localPageList, setLocalPageList] = useState<PageListType>(pageList);
  // 加载更多状态
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // 列表刷新加载状态
  const [isRefreshing, setIsRefreshing] = useState(false);
  // 刷新时间戳，用于触发列表重新渲染和闪烁效果
  const [refreshKey, setRefreshKey] = useState(Date.now());

  useEffect(() => {
    const g_params = {
      event: "productListPage",
      remarketing_params: {
        ecomm_prodid: "",
        ecomm_pagetype: "category",
        ecomm_totalvalue: "",
      },
      params: { userId: null, categoryId: null },
      emarketing_params: {
        pagetype: "",
        productid: "",
        productsku: "",
        searchterm: "",
        category: breadCrumb[0].name || "",
        cartinfo: "",
        orderid: "",
        customerid: getCookie("userCustomerId") || null,
        customeremail: localStorage.getItem("userEmail") || null,
        orderinfo: "",
      },
    };

    setGtmDataLayer(g_params);


    const url = breadCrumb.length ? breadCrumb[breadCrumb.length - 1].url : ''
    if (url) {
      matomoSetCustomDimension(28, url)
    }
  }, []);

  // 当父组件传入的 sidebarFilter 变化时，同步更新本地状态
  useEffect(() => {
    let sidebarFilterFormatter = sidebarFilter ? sidebarFilter : [];

    // 只保留key为"category"、"price-range"和"hot-brand"的元素
    sidebarFilterFormatter = sidebarFilterFormatter.filter(
      (item) =>
        item.key === "category" ||
        item.key === "price-range" ||
        item.key === "hot-brand"
    );

    // 插入一个新的元素
    sidebarFilterFormatter.push({
      key: "popularity",
      title: "Popularity",
    });

    setLocalSidebarFilter(sidebarFilterFormatter);
  }, [sidebarFilter]);

  // 当上层 props 发生变化时，同步初始化本地参数与数据
  useEffect(() => {
    setQueryParams((prev: any) => ({
      ...prev,
      categoryPath,
      otherParam,
      page: pageList?.page ? pageList.page : 1,
      perPage: 16,
    }));
    setLocalPageList(pageList);
  }, [categoryPath, otherParam, pageList]);
  // 添加展示模式状态，默认为网格模式（一行两个商品）
  const [isGridMode, setIsGridMode] = useState(true);

  // 根据参数请求并刷新列表
  const refreshList = async (nextParams: any, isLoadMore: boolean = false) => {
    // 非加载更多时显示蒙层
    if (!isLoadMore) {
      setIsRefreshing(true);
    }

    try {
      const data = await getCategoryList(nextParams);
      if (data) {
        const defaultPageList = {
          items: [],
          total: 0,
          page: nextParams.page ? nextParams.page : 1,
          perPage: nextParams.perPage ? nextParams.perPage : 24,
        };
        const newPageList = data.pageList ? data.pageList : defaultPageList;

        if (isLoadMore) {
          // 加载更多时，追加数据到现有列表
          setLocalPageList((prev) => ({
            ...prev,
            items: [...prev.items, ...newPageList.items],
            total: newPageList.total, // 更新总数
          }));
        } else {
          // 普通刷新时，替换数据并更新刷新key以触发闪烁效果
          setLocalPageList(newPageList);
          setRefreshKey(Date.now());
        }
      }
    } finally {
      // 请求完成后隐藏蒙层
      if (!isLoadMore) {
        setIsRefreshing(false);
      }
    }
  };

  // 切换展示模式
  const toggleDisplayMode = () => {
    setIsGridMode(!isGridMode);
  };

  // 加载更多处理函数
  const handleLoadMore = async () => {
    if (isLoadingMore) return;

    setIsLoadingMore(true);

    const nextPage = (queryParams.page || 1) + 1;
    const nextParams = {
      ...queryParams,
      page: nextPage,
    };

    if (!nextParams.sortBy) {
      nextParams.sortBy = "popularity";
      nextParams.sortDirection = "desc";
    }

    try {
      await refreshList(nextParams, true);
      setQueryParams((prev) => ({ ...prev, page: nextPage }));
    } catch (error) {
      console.error("加载更多失败:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // 处理筛选变化的回调函数（单选）：任何筛选或排序都会覆盖之前相同类型的值
  const handleSortChange = (option: {
    type: string;
    value?: string;
    item?: any;
  }) => {
    // 直接基于当前的 queryParams 构建新参数
    const nextParams: any = { ...queryParams };

    // 清理排序和筛选相关参数
    delete nextParams.freeShipping;
    delete nextParams.sortBy;
    delete nextParams.sortDirection;

    switch (option.type) {
      case "category": {
        window.location.href = `/category${option?.item?.url || ""}`;
        return; // 跳转后直接返回
      }
      case "price-range": {
        // 直接使用 item 上的 minPrice/maxPrice（单选覆盖）
        delete nextParams.priceRange;
        delete nextParams.minPrice;
        delete nextParams.maxPrice;

        const itemMin = option?.item?.minPrice;
        const itemMax = option?.item?.maxPrice;

        if (typeof itemMin === "number") nextParams.minPrice = itemMin;
        if (typeof itemMax === "number") nextParams.maxPrice = itemMax;
        nextParams.page = 1;
        break;
      }
      case "hot-brand": {
        let brandValue = "";
        if (option?.item?.name) {
          brandValue = option.item.name;
        } else if (option?.value) {
          brandValue = option.value;
        }

        if (brandValue === "") {
          delete nextParams.brand;
        } else {
          nextParams.brand = brandValue;
        }
        nextParams.page = 1;
        break;
      }
      case "filter": {
        // 目前仅支持免邮筛选
        const filterValue = option?.value;
        if (filterValue === "freeshipping") {
          nextParams.freeShipping = 1;
        }
        nextParams.page = 1;
        break;
      }
      case "popularity": {
        // 侧边栏的"Popularity"快捷项
        nextParams.sortBy = "popularity";
        nextParams.sortDirection = "desc";
        nextParams.page = 1;
        break;
      }
      case "sort": {
        // 顶部四个排序按钮：bestMatch / price-low-to-high / price-high-to-low / new-arrivals
        if (option.value === "bestMatch") {
          nextParams.sortBy = "bestMatch";
          nextParams.sortDirection = "desc";
        } else if (option.value === "price-low-to-high") {
          nextParams.sortBy = "price";
          nextParams.sortDirection = "asc";
        } else if (option.value === "price-high-to-low") {
          nextParams.sortBy = "price";
          nextParams.sortDirection = "desc";
        } else if (option.value === "new-arrivals") {
          // 后端按需调整字段名
          nextParams.sortBy = "addTime";
          nextParams.sortDirection = "desc";
        } else if (!option.value) {
          nextParams.sortBy = "popularity";
          nextParams.sortDirection = "desc";
        }
        nextParams.page = 1;
        break;
      }
      default:
        break;
    }

    if (!nextParams.sortBy) {
      nextParams.sortBy = "popularity";
      nextParams.sortDirection = "desc";
    }

    // 更新状态并刷新列表
    setQueryParams(nextParams);
    refreshList(nextParams);
  };

  return (
    <div className={styles["comp-category-content"]}>
      <div className={styles["content-title"]}>
        <div className={styles["category-name"]}>
          {breadCrumb &&
            breadCrumb.length > 0 &&
            (() => {
              const currentItem = breadCrumb.find((item) => item.current);
              if (currentItem && currentItem.name) {
                return currentItem.name;
              }
              return breadCrumb[breadCrumb.length - 1].name;
            })()}

          {/* 展示数量 */}
          {pageList.total > 0 && <span>&nbsp;({pageList.total})</span>}
        </div>
        <Image
          src={`${process.env.IMAGE_DOMAIN}/svg/category-page/${
            isGridMode ? "toggle-column.svg" : "toggle-grid.svg"
          }`}
          alt="Grid"
          className={styles["display-toggle"]}
          onClick={toggleDisplayMode}
          width={20}
          height={20}
        />
      </div>

      <FilterBox
        onSortChange={handleSortChange}
        sidebarFilter={localSidebarFilter}
      />

      {/* 产品列表 */}
      {localPageList &&
      localPageList.items &&
      localPageList.items.length > 0 ? (
        <>
          <div className={styles["product-list-container"]}>
            {/* 加载蒙层 */}
            {(isRefreshing || isLoadingMore) && (
              <div className={styles["loading-overlay"]}>
                <Loading color="#D90B17" style={{ marginTop: '100px' }} />
              </div>
            )}
            
            <div
              className={
                isGridMode ? styles["product-list"] : styles["product-list-full"]
              }
            >
              {localPageList.items.map((product: ProductType, index: number) => (
                <div
                  className={
                    isGridMode
                      ? styles["product-item"]
                      : styles["product-item-full"]
                  }
                  key={`${product.productID}-${refreshKey}-${index}`}
                >
                  {isGridMode ? (
                    <ProductItem product={product} />
                  ) : (
                    <ProductCardFullWidth product={product} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 加载更多按钮 */}
          {localPageList.items.length < localPageList.total && (
            <>
              {
                isLoadingMore ? (
                  <div className={styles['loading-icon']}>
                    <div className={styles['loading-icon-box']}>
                      <span></span>
                    </div>
                  </div>
                ) : (
                  <Image 
                    src={'https://img.crazysales.com.au/images/newhomepage/mobile/svg/main/icon-viewmore.svg'}
                    width={200}
                    height={42}
                    style={{ display: 'block', margin: '16px auto' }}
                    alt="empty"
                    onClick={handleLoadMore}
                  />
                )
              }
            </>
          )}
        </>
      ) : (
        <div className={styles["empty-list"]}>No item found.</div>
      )}
    </div>
  );
}
